import {Stack, TextInput} from '@mantine/core';
import React, {useEffect, useMemo} from 'react';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {
  BioentityLinkFieldWithTargetSchema,
  isRequiredLinkStaticSchemaInterface,
} from 'schemas/fields/SchemaFieldConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {Optional} from 'util/Types';

const MAX_NAME_LENGTH = 255;

export interface CreatePartFormProps {
  name: string;
  folderId: Optional<string>;
  partLinkField: BioentityLinkFieldWithTargetSchema | null;
  partLinkFields: PartLinkFields | null;
  onNameChange: (name: string) => void;
  onFolderIdChange: (folderId: string) => void;
  onPartLinkFieldChange: (partLinkField: BioentityLinkFieldWithTargetSchema | null) => void;
}

export type PartLinkFields = Array<{
  field: BioentityLinkFieldWithTargetSchema;
  hasExistingValue: boolean;
}>;

export function getCreateFormValidation({name, folderId}: {name: string; folderId: Optional<string>}): {
  isValid: boolean;
  nameIsTooLong: boolean;
  nameErrorMessage?: string;
} {
  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const nameErrorMessage = nameIsTooLong
    ? `Names can be at most 255 characters long (currently ${name.length})`
    : undefined;

  return {
    isValid: !nameOrFolderMissing && !nameIsTooLong,
    nameIsTooLong,
    nameErrorMessage,
  };
}

function getDefaultPartLinkField(options: Array<OptionData>): BioentityLinkFieldWithTargetSchema | null {
  const nonNullSelectableOptions = options.filter((option) => !option.disabled && option.value != null);
  return nonNullSelectableOptions.length === 1 ? nonNullSelectableOptions[0].value : null;
}

function getPartLinkFieldOptions(partLinkFields: PartLinkFields): Array<OptionData> {
  const partLinkFieldOptions: Array<OptionData> = partLinkFields.map(({field, hasExistingValue}) => {
    let disabledMessage;
    if (!field.isMulti && hasExistingValue) {
      disabledMessage = 'Single link field already has a value.';
    } else if (isRequiredLinkStaticSchemaInterface(field.requiredLink)) {
      // TODO (BNCH-138848): Implement
      disabledMessage = 'Setting an SSI part link field is not yet supported.';
    }
    return {
      name: field.name,
      value: field,
      disabledMessage,
      disabled: disabledMessage != null,
    };
  });

  partLinkFieldOptions.unshift({
    name: 'None',
    value: null,
    disabled: false,
  });
  return partLinkFieldOptions;
}

export default function CreatePartForm({
  name,
  folderId,
  partLinkField,
  partLinkFields,
  onNameChange,
  onFolderIdChange,
  onPartLinkFieldChange,
}: CreatePartFormProps): JSX.Element {
  const {nameErrorMessage} = getCreateFormValidation({name, folderId});

  const partLinkFieldOptions = useMemo(
    () =>
      partLinkFields != null && partLinkFields.length > 0 ? getPartLinkFieldOptions(partLinkFields) : null,
    [partLinkFields]
  );

  useEffect(() => {
    if (partLinkField == null && partLinkFieldOptions != null) {
      const defaultPartLinkField = getDefaultPartLinkField(partLinkFieldOptions);
      if (defaultPartLinkField != null) {
        onPartLinkFieldChange(defaultPartLinkField);
      }
    }
  }, [partLinkField, partLinkFieldOptions, onPartLinkFieldChange]);

  return (
    <Stack spacing="0">
      <FormGroup>
        <FieldLabel label="Part name" isRequired={true} htmlFor="name" />
        <TextInput
          id="name"
          autoFocus={true}
          placeholder="Name"
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          value={name}
          error={nameErrorMessage}
          onFocus={(e) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          aria-label="Part name"
        />
      </FormGroup>

      {partLinkFieldOptions != null && partLinkFieldOptions.length > 0 && (
        <FormGroup>
          <FieldLabel label="Part link field" isRequired={false} />
          <SelectDropdown
            displayName={partLinkField?.name ?? 'Select a part link field'}
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FIT_CONTENT}
                onSelect={(value: BioentityLinkFieldWithTargetSchema | null) => {
                  onPartLinkFieldChange(value);
                }}
                optionDatas={partLinkFieldOptions}
                selectedValue={partLinkField}
              />
            }
          />
        </FormGroup>
      )}

      <FormGroup>
        <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
        <FolderSelector
          buttonSize={ButtonSize.MEDIUM}
          enableRedesign={true}
          showInventory={true}
          value={folderId}
          onChange={(e) => {
            onFolderIdChange(e.target.value);
          }}
          unsetOnFetchFail={true}
          allowNone={false}
        />
      </FormGroup>
    </Stack>
  );
}
